import React, { useState, useEffect } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';

import { usePostHog } from 'posthog-js/react'

interface RetentionRates {
  first: number;
  second: number;
  third: number;
  subsequent: number;
}

interface CalculatorProps {
  onCalculate: () => void;
  isAuthenticated: boolean;
  retentionRates: RetentionRates;
  setRetentionRates: React.Dispatch<React.SetStateAction<RetentionRates>>;
  price: number;
  setPrice: React.Dispatch<React.SetStateAction<number>>;
  currentCustomerArpu: number;
  setCurrentCustomerArpu: React.Dispatch<React.SetStateAction<number>>;
}

const Calculator: React.FC<CalculatorProps> = ({ 
  onCalculate, 
  isAuthenticated, 
  retentionRates, 
  setRetentionRates,
  price,
  setPrice,
  currentCustomerArpu,
  setCurrentCustomerArpu
}) => {

  const posthog = usePostHog()

  const [currency, setCurrency] = useState<string>(() => {
    const savedCurrency = localStorage.getItem('ltv_currency');
    return savedCurrency || '€';
  });

  const [frequency, setFrequency] = useState<'monthly' | 'quarterly' | 'annual'>(() => {
    const savedFrequency = localStorage.getItem('ltv_frequency');
    return (savedFrequency as 'monthly' | 'quarterly' | 'annual') || 'monthly';
  });

  const [results, setResults] = useState<{
    sixMonth: number;
    twelveMonth: number;
    twentyFourMonth: number;
    lifetime: number;
  } | null>(null);

  useEffect(() => {
    localStorage.setItem('ltv_currency', currency);
  }, [currency]);

  useEffect(() => {
    localStorage.setItem('ltv_frequency', frequency);
  }, [frequency]);

  // Helper function to calculate payments per year based on frequency
  const getPaymentsPerYear = (freq: 'monthly' | 'quarterly' | 'annual'): number => {
    switch (freq) {
      case 'monthly':
        return 12;
      case 'quarterly':
        return 4;
      case 'annual':
        return 1;
      default:
        return 12;
    }
  };

  // Calculate revenue for a specific period
  const calculateLTV = (
    basePrice: number,
    freq: 'monthly' | 'quarterly' | 'annual',
    months: number,
    rates: RetentionRates
  ): number => {
    const paymentsPerYear = getPaymentsPerYear(freq);
    const paymentInterval = 12 / paymentsPerYear;
    const numberOfPayments = Math.ceil(months / paymentInterval);
    let ltv = 0;
    let lastPeriodUsers = 100;


    for (let i = 0; i < numberOfPayments; i++) {
      let currentPeriodRevenue;
      if (i === 0) {
        currentPeriodRevenue = basePrice * (rates.first / 100);
        lastPeriodUsers = rates.first;
      } else if (i === 1) {
        currentPeriodRevenue = basePrice * (rates.second / 100);
        lastPeriodUsers = rates.second;
      } else if (i === 2) {
        currentPeriodRevenue = basePrice * (rates.third / 100);
        lastPeriodUsers = rates.third;
      } else {
        const currentPeriodUsers = lastPeriodUsers * ((100 - rates.subsequent) / 100);
        currentPeriodRevenue = basePrice * (currentPeriodUsers / 100);
        lastPeriodUsers = currentPeriodUsers;
      }
      
      ltv += currentPeriodRevenue;
    }

    
    return ltv;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate inputs
    if (!price || !retentionRates.first || !retentionRates.second || 
        !retentionRates.third || !retentionRates.subsequent) {
      alert('Please fill in all fields with valid numbers');
      return;
    }

    // Calculate revenues for different periods
    const sixMonthRev = calculateLTV(price, frequency, 6, retentionRates);
    const twelveMonthRev = calculateLTV(price, frequency, 12, retentionRates);
    const twentyFourMonthRev = calculateLTV(price, frequency, 24, retentionRates);
    const lifetimeRev = calculateLTV(price, frequency, 120, retentionRates);

    setResults({
      sixMonth: sixMonthRev,
      twelveMonth: twelveMonthRev,
      twentyFourMonth: twentyFourMonthRev,
      lifetime: lifetimeRev
    });

    onCalculate();

    posthog.capture('submit_ltv_calculator', { 
      frequency: frequency,
      retention_rates: retentionRates,
      current_customer_arpu: currentCustomerArpu
    });
  };

  const handleRetentionChange = (field: keyof RetentionRates, value: string) => {
    let numValue = parseFloat(value);
    if (numValue < 0) numValue = 0;
    if (numValue > 100) numValue = 100;
    
    setRetentionRates(prev => ({
      ...prev,
      [field]: numValue
    }));
  };

  return (
    <div className="calculator">
      <h2>Customer Lifetime Value Calculator</h2>
      <p style={{marginBottom: '2rem', marginTop: '1rem'}}>Takes into account negative compounding and variable retention rates that are usually higher in the first months and calculates realistic LTV, so you can better plan your costs and strategy.</p>
      <form className="calculator-form" onSubmit={handleSubmit}>
        <div className="input-group">
          <div className="price-input">
            <label>
              Return per Customer (ARPU) ({currency})
              <div className="price-with-currency">
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="currency-select"
                >
                  <option value="$">$</option>
                  <option value="€">€</option>
                  <option value="£">£</option>
                  <option value="¥">¥</option>
                </select>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(Number(e.target.value))}
                  min="0"
                />
              </div>
            </label>
          </div>
        </div>

        <div className="input-group">
          <label htmlFor="payment-frequency">Payment Frequency</label>
          <select
            id="payment-frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as 'monthly' | 'quarterly' | 'annual')}
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="annual">Annual</option>
          </select>
        </div>

        <div className="input-group">
          <label htmlFor="first-payment">First Payment Made by % of the Clients Signed Up</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="first-payment-slider"
              value={retentionRates.first || 0}
              onChange={(e) => handleRetentionChange('first', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="first-payment"
              value={retentionRates.first || ''}
              onChange={(e) => handleRetentionChange('first', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="95"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: If 100 clients sign up and this is {retentionRates.first}%, then {retentionRates.first} clients will make their first payment of {currency}{price}, totaling {currency}{(retentionRates.first * price).toLocaleString()}
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="second-payment">Second Payment Made by % of the Clients Signed Up</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="second-payment-slider"
              value={retentionRates.second || 0}
              onChange={(e) => handleRetentionChange('second', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="second-payment"
              value={retentionRates.second || ''}
              onChange={(e) => handleRetentionChange('second', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="90"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: If 100 clients signed up initially, {retentionRates.second} clients will make their second payment of {currency}{price}, totaling {currency}{(retentionRates.second * price).toLocaleString()}
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="third-payment">Third Payment Made by % of the Clients Signed Up</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="third-payment-slider"
              value={retentionRates.third || 0}
              onChange={(e) => handleRetentionChange('third', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="third-payment"
              value={retentionRates.third || ''}
              onChange={(e) => handleRetentionChange('third', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="85"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: If 100 clients signed up initially, {retentionRates.third} clients will make their third payment of {currency}{price}, totaling {currency}{(retentionRates.third * price).toLocaleString()}
          </small>
        </div>

        <div className="input-group">
          <label htmlFor="subsequent-payment">Subsequent Churn (%)</label>
          <div className="slider-input-group">
            <input
              type="range"
              id="subsequent-payment-slider"
              value={retentionRates.subsequent || 0}
              onChange={(e) => handleRetentionChange('subsequent', e.target.value)}
              min="0"
              max="100"
              step="1"
              className="slider"
            />
            <input
              type="number"
              id="subsequent-payment"
              value={retentionRates.subsequent || ''}
              onChange={(e) => handleRetentionChange('subsequent', e.target.value)}
              min="0"
              max="100"
              step="0.1"
              placeholder="10"
              required
              className="number-input"
            />
            <span>%</span>
          </div>
          <small className="helper-text">
            Example: If you have {retentionRates.third} clients after the third payment and the churn is {retentionRates.subsequent}%, you'll keep {Math.round(retentionRates.third * (100 - retentionRates.subsequent) / 100)} clients for the next payment, totaling {currency}{Math.round(retentionRates.third * (100 - retentionRates.subsequent) / 100 * price).toLocaleString()}
          </small>
        </div>

        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button type="submit" className="calculate-button">
            Calculate LTV
          </button>
          {results && (
            <button
              type="button"
              className="icon-button"
              onClick={() => {
                const resultsText = `
                  SaaSSy.Tools - LTV Calculator Results

                  Settings:
                  ARPU: €${currentCustomerArpu.toLocaleString()} per ${frequency}
                  Retention Rates: 
                  First Payment: ${retentionRates.first}% 
                  Second Payment: ${retentionRates.second}%
                  Third Payment: ${retentionRates.third}%
                  Subsequent Churn: ${retentionRates.subsequent}% per ${frequency}

                  Results:
                  6-Month LTV: €${results.sixMonth.toLocaleString()}
                  12-Month LTV: €${results.twelveMonth.toLocaleString()}
                  24-Month LTV: €${results.twentyFourMonth.toLocaleString()}
                  Lifetime LTV: €${results.lifetime.toLocaleString()}
                  `;
                navigator.clipboard.writeText(resultsText).then(() => {
                  toast.success('Results copied to clipboard!', {
                    duration: 2000,
                    position: 'bottom-right',
                  });
                });

                posthog.capture('copy_ltv_data', { 
                  frequency: frequency,
                  retention_rates: retentionRates,
                  current_customer_arpu: currentCustomerArpu
                });

              }}
              title="Copy results"
            >
              <ClipboardDocumentIcon className="icon" />
            </button>
          )}
        </div>
      </form>

      <Toaster />

      {results && (
        <div id="results">
          <div className="result-item">
            <h3>6-Month LTV</h3>
            <p className="result-value">
              {currency}{results.sixMonth.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
          </div>
          <div className="result-item">
            <h3>12-Month LTV</h3>
            <p className="result-value">
              {currency}{results.twelveMonth.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
          </div>
          <div className="result-item">
            <h3>24-Month LTV</h3>
            <p className="result-value">
              {currency}{results.twentyFourMonth.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
          </div>
          <div className="result-item">
            <h3>Lifetime LTV</h3>
            <p className="result-value">
              {currency}{results.lifetime.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Calculator;
